/**
 * Template Types
 *
 * Canonical source of truth for the ProjectTemplate data model.
 * All other files (TemplateContext, form sheets, pages) must import from here.
 *
 * Architecture Decision 1: This file is the single source of truth.
 * TemplateContext.tsx mirrors this interface exactly.
 */

/**
 * A cost centre entry as stored on a template.
 *
 * Architecture Decision: Templates store the full cost centre definition
 * (identical to what CreateCostCentrePanel produces), so that when a project
 * is created from a template the cost centres are copied across with complete
 * fidelity — no post-creation editing required.
 *
 * Runtime-only fields that the API generates (id, actualCosts, committedCosts,
 * actualRevenue, actualHours, lastUpdated, createdAt, updatedAt) are excluded
 * here — they are populated by the backend when the project is created.
 *
 * Engineering team note: The backend endpoint that creates a project from a
 * template must accept this shape for each cost centre and persist it.
 * See Phase 6 engineering notes in PROJECT_TEMPLATES_PRD.md.
 */
export interface TemplateCostCentre {
  /** Unique alphanumeric code for this cost centre, e.g. "ENG-001". Uppercase. */
  code: string;
  /** Display name, e.g. "Engineering". Min 3 chars. */
  name: string;
  /** Department | Project | Product | Service | Location | Division | Team | Other */
  type?: string;
  /** Whether this cost centre can be billed to clients. Defaults true. */
  billable: boolean;
  /** Person responsible for this cost centre. */
  owner?: string;
  /** Optional longer description. */
  description?: string;
  /** Lifecycle status of the cost centre. */
  status?: 'Not Started' | 'In Progress' | 'On Hold' | 'Completed' | 'Cancelled';
  /** Initial costs budget in the project currency. 0 = not set. */
  costsBudget: number;
  /** Initial revenue budget in the project currency. 0 = not set. */
  revenueBudget: number;
  /** Initial hours budget. 0 = not set. */
  hoursBudget: number;
  /** Optional custom key-value metadata. */
  customFields?: Record<string, string>;
  /**
   * References the `code` of the parent cost centre in this template's cost centre list.
   * Enables the Structure View hierarchy in the template form.
   * When the project is created from this template, the backend resolves these
   * code-based parent references to real parentId integers on the project's cost centres.
   * null = top-level (no parent).
   *
   * Engineering note: On project creation, walk the template's costCentres array
   * in order, insert each as a CostCentre row, then do a second pass to set parentId
   * by looking up the newly-inserted IDs by code.
   */
  parentCode?: string | null;
}

/**
 * A resource budget entry as stored on a template.
 *
 * Architecture Decision 9 (revised): Templates store the list of Roles only.
 * Hours and rates are NOT stored on the template — hours are distributed monthly
 * on the live project after creation, and the cost rate is derived from the
 * Roles & Rates system when a project is created from the template.
 * See PROJECT_TEMPLATES_PRD.md Decision 9.
 *
 * Engineering team note: When creating a project from a template, for each entry
 * create a ResourceBudget row with budgetHours = 0, billRate derived from Roles &
 * Rates, monthlyHours = [], and all runtime fields (actualHours, actualCost, etc.)
 * set to 0. See Phase 8.E in PROJECT_TEMPLATES_PRD.md.
 */
export interface TemplateResourceEntry {
  /** Role name, e.g. "Project Manager". Matches roles in the Roles & Rates system. */
  role: string;
}

/**
 * Template categories used for grouping and filtering.
 */
export type TemplateCategory =
  | 'Consulting'
  | 'Fixed-Fee Delivery'
  | 'Infrastructure'
  | 'Marketing'
  | 'Internal'
  | 'Other';

export const TEMPLATE_CATEGORIES: TemplateCategory[] = [
  'Consulting',
  'Fixed-Fee Delivery',
  'Infrastructure',
  'Marketing',
  'Internal',
  'Other',
];

export interface ProjectTemplate {
  id: string;
  /** Required — unique display name for the template. Max 80 chars. */
  templateName: string;
  /** Optional — describes when to use this template. Max 200 chars. */
  templateDescription?: string;
  /** Groups templates for browsing and filtering. */
  category?: TemplateCategory;
  /** Whether the user has starred this template. */
  isFavorite: boolean;
  /** ISO date string */
  createdAt: string;
  /** ISO date string */
  updatedAt: string;

  // ── General Information ───────────────────────────────────────────────────
  /** Pre-fills the project Description field. Max 500 chars. */
  description?: string;
  /** Low | Medium | High | Critical — applied capitalised, not lowercased */
  priority?: string;
  /** Fixed Price | Time & Materials */
  type?: string;
  /** Status ID from the system status list. Pre-selects project Status on creation. */
  defaultStatus?: string;

  // ── Timeline ──────────────────────────────────────────────────────────────
  /**
   * Relative duration in days. When applied and a start date is set,
   * end date auto-calculates as startDate + defaultDurationDays.
   * Positive integer, max 3650. null = not set.
   */
  defaultDurationDays?: number;

  // ── Management ────────────────────────────────────────────────────────────
  /**
   * Default Project Manager — an employee ID (string) or display name.
   * If the value matches an employee ID in the system, the Project Manager
   * field is pre-filled when the project is created from this template.
   * Stored as a string so it remains valid even before the backend is wired.
   *
   * Engineering note: When the backend API returns template data, this field
   * should be the employee's UUID/ID (same format as the Key People combobox uses).
   * See Phase 6 backend notes in PROJECT_TEMPLATES_PRD.md.
   */
  defaultProjectManager?: string;
  /** GBP | USD | EUR | AUD | CAD | JPY | CHF */
  currencyCode?: string;
  /** Optional default client. Only set for templates that are always for one client. */
  clients?: string;
  /** Cost centres copied to the project on creation. */
  costCentres?: TemplateCostCentre[];
  /**
   * Resource budget roles copied to the project's Resource Budget tab on creation.
   * Stores the list of roles only — hours are entered monthly on the project and
   * rates are derived from Roles & Rates. See TemplateResourceEntry and Architecture Decision 9.
   */
  resourceBudget?: TemplateResourceEntry[];

  // ── Billing Configuration ─────────────────────────────────────────────────
  /**
   * Whether projects created from this template are billable by default.
   * Defaults to true. PMs can override at project creation time.
   */
  billable?: boolean;

  /**
   * Billing configuration fields are driven by the existing `type` field
   * ('fixed-price' | 'time-and-materials'). There is no separate billingType field —
   * the project Type IS the billing type. The fields below are conditionally shown
   * based on the value of `type`.
   *
   * - type = 'time-and-materials' → billingFrequency, invoiceTemplateId, invoiceEmailTemplateId, creditNoteEmailTemplateId
   * - type = 'fixed-price'        → billingTriggerType, completionCalcMethod, invoiceTemplateId, invoiceEmailTemplateId, creditNoteEmailTemplateId
   */

  // ── T&M-specific fields ───────────────────────────────────────────────────
  /**
   * How often invoices are generated (T&M projects only).
   * Maps to BillingFrequency in billingService.ts.
   */
  billingFrequency?: 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'on-completion' | 'milestone';

  // ── Fixed Fee-specific fields ─────────────────────────────────────────────
  /**
   * When billing milestones fire (Fixed Fee projects only).
   * - 'date'             → milestone fires on its dueDate
   * - 'percent-complete' → milestone fires when completionPercent >= triggerPercent
   */
  billingTriggerType?: 'date' | 'percent-complete';
  /**
   * How project completion % is calculated (Fixed Fee only).
   * - 'manual'           → PM enters % manually
   * - 'tasks'            → derived from completed tasks
   * - 'hours'            → derived from hours logged vs budgeted
   * - 'milestones'       → derived from completed milestones
   */
  completionCalcMethod?: 'manual' | 'tasks' | 'hours' | 'milestones';

  // ── Shared billing fields (both T&M and Fixed Fee) ────────────────────────
  /**
   * ID of the invoice document template to use for this project.
   * References InvoiceTemplate.id from InvoiceTemplateContext.
   */
  invoiceTemplateId?: number | null;
  /**
   * ID of the email template used when sending invoices.
   * References EmailTemplate.id from EmailTemplateContext (type: 'invoice').
   */
  invoiceEmailTemplateId?: string | null;
  /**
   * ID of the email template used when sending credit notes.
   * References EmailTemplate.id from EmailTemplateContext (type: 'credit_note').
   */
  creditNoteEmailTemplateId?: string | null;

  // ── Content ───────────────────────────────────────────────────────────────
  /** Pre-fills the project Objectives field. Max 1000 chars. */
  objectives?: string;
  /** Pre-fills the project Key Deliverables field. Max 1000 chars. */
  keyDeliverables?: string;

  // ── Usage Tracking (system-managed) ──────────────────────────────────────
  /** How many projects have been created from this template. */
  usageCount?: number;
  /** ISO date string or null — when the template was last applied to a project. */
  lastUsedAt?: string;
}

export interface CreateTemplateData {
  templateName: string;
  templateDescription?: string;
  category?: TemplateCategory;
  description?: string;
  priority?: string;
  type?: string;
  defaultStatus?: string;
  defaultDurationDays?: number;
  defaultProjectManager?: string;
  currencyCode?: string;
  clients?: string;
  costCentres?: TemplateCostCentre[];
  resourceBudget?: TemplateResourceEntry[];
  billable?: boolean;
  billingFrequency?: 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'on-completion' | 'milestone';
  billingTriggerType?: 'date' | 'percent-complete';
  completionCalcMethod?: 'manual' | 'tasks' | 'hours' | 'milestones';
  invoiceTemplateId?: number | null;
  invoiceEmailTemplateId?: string | null;
  creditNoteEmailTemplateId?: string | null;
  objectives?: string;
  keyDeliverables?: string;
}

export interface UpdateTemplateData extends Partial<CreateTemplateData> {
  id: string;
}
